import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Typography,
  FormHelperText,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  Email as EmailIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";
import { z } from "zod";
import { UserRoleEnum, UserDetail } from "@shared/schemas";
import { useCancelEmailChange } from "../../features/users/hooks";

// For edit mode, we use a modified schema with all fields optional
const UpdateUserFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email format"),
  role: UserRoleEnum,
  mfaEnabled: z.boolean().default(false),
  mfaType: z.string().optional(),
});

// For create mode, we remove password and add sendOnboardingEmail
const CreateUserFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email format"),
  role: UserRoleEnum,
  mfaEnabled: z.boolean().default(false),
  mfaType: z.string().optional(),
  sendOnboardingEmail: z.boolean().default(false),
});

// Use appropriate schema based on mode
type UserFormValues = z.infer<typeof CreateUserFormSchema>;

interface UserFormProps {
  user?: UserDetail;
  isEditMode: boolean;
  onSubmit: (data: UserFormValues) => Promise<void>;
  isLoading: boolean;
  error?: string;
  onUserUpdate?: () => void; // Callback to refresh user data after cancel
  onCancel?: () => void; // Callback to handle cancel action
}

/**
 * Component for creating or editing a user
 */
const UserForm: React.FC<UserFormProps> = ({
  user,
  isEditMode,
  onSubmit,
  isLoading,
  error,
  onUserUpdate,
  onCancel,
}) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const { cancelEmailChange, isLoading: isCanceling } = useCancelEmailChange();
  // Set up form with validation
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UserFormValues>({
    resolver: zodResolver(
      isEditMode ? UpdateUserFormSchema.partial() : CreateUserFormSchema,
    ),
    defaultValues: user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          mfaEnabled: user.mfaEnabled,
          mfaType: user.mfaType || undefined,
        }
      : {
          firstName: "",
          lastName: "",
          email: "",
          role: "USER" as const,
          mfaEnabled: false,
          sendOnboardingEmail: false,
        },
  });

  // Form submission handler
  const handleFormSubmit = async (data: UserFormValues) => {
    await onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                type="email"
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </Grid>

        {/* Pending Email Display */}
        {isEditMode && user?.pendingEmail && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: "2px solid",
                borderColor: "#f57c00", // Darker yellow/orange border
                borderRadius: 1,
                bgcolor: "#fffde7", // Light yellow background
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PendingIcon sx={{ color: "#f57c00", mr: 1 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    Email Change Pending
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Waiting for verification:{" "}
                    <strong>{user.pendingEmail}</strong>
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Cancel pending email change">
                <IconButton
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={isCanceling}
                  size="small"
                  sx={{ color: "#f57c00" }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        )}

        {!isEditMode && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<EmailIcon />}>
              A temporary password will be generated and sent to the user via
              onboarding email.
            </Alert>
          </Grid>
        )}

        {/* Account Settings */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Account Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select {...field} labelId="role-label" label="Role">
                  <MenuItem value="ADMIN">Admin</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
                  <MenuItem value="SERVER">Server</MenuItem>
                  <MenuItem value="USER">User</MenuItem>
                </Select>
                {errors.role && (
                  <FormHelperText>{errors.role.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="mfaEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch checked={field.value} onChange={field.onChange} />
                }
                label="Enable Multi-Factor Authentication"
              />
            )}
          />
        </Grid>

        {/* Onboarding Email Toggle - Only for create mode */}
        {!isEditMode && (
          <Grid item xs={12}>
            <Controller
              name="sendOnboardingEmail"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Send onboarding email immediately
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        If disabled, you can send the onboarding email later
                        from the user list or details page
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Grid>
        )}

        {/* Submit Buttons */}
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                } else {
                  reset();
                }
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading
                ? "Saving..."
                : isEditMode
                  ? "Update User"
                  : "Create User"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Cancel Email Change Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        aria-labelledby="cancel-email-dialog-title"
        aria-describedby="cancel-email-dialog-description"
      >
        <DialogTitle id="cancel-email-dialog-title">
          Cancel Email Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-email-dialog-description">
            Are you sure you want to cancel the pending email change to{" "}
            <strong>{user?.pendingEmail}</strong>? This will remove the pending
            email and keep the current email address.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isCanceling}
          >
            Keep Pending
          </Button>
          <Button
            onClick={async () => {
              if (user?.id) {
                const result = await cancelEmailChange(user.id);
                if (result.success) {
                  setNotification({
                    message: "Email change cancelled successfully",
                    severity: "success",
                  });
                  onUserUpdate?.(); // Refresh user data
                } else {
                  setNotification({
                    message: result.error || "Failed to cancel email change",
                    severity: "error",
                  });
                }
              }
              setCancelDialogOpen(false);
            }}
            color="warning"
            disabled={isCanceling}
            autoFocus
          >
            {isCanceling ? "Canceling..." : "Cancel Change"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Alert */}
      {notification && (
        <Alert
          severity={notification.severity}
          sx={{ mt: 2 }}
          onClose={() => setNotification(null)}
        >
          {notification.message}
        </Alert>
      )}
    </Box>
  );
};

export default UserForm;
